import React, { useState, useEffect, useRef } from 'react';
import { FiInfo, FiPlay, FiPause, FiRefreshCw } from 'react-icons/fi';
import IconWrapper from './IconWrapper';

// Define types for our historical data
interface DimensionData {
  signal: number;
  velocity: number;
  sentimentBalance: number;
  harmDetection: number;
  vulnerability: number;
  rootCauseExpansion: number;
}

type TimeRange = '7d' | '14d' | '30d';

const RadarView: React.FC = () => {
  // Default state for initial load
  const initialData: DimensionData = {
    signal: 74,
    velocity: 92,
    sentimentBalance: 68,
    harmDetection: 38,
    vulnerability: 61,
    rootCauseExpansion: 79
  };
  
  // State for current displayed data
  const [dimensions, setDimensions] = useState<DimensionData>(initialData);
  
  // State for animation controls
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [currentFrame, setCurrentFrame] = useState(0);
  const [animationData, setAnimationData] = useState<DimensionData[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const FRAME_DURATION = 200; // ms between frames (5 frames per second)
  
  // Helper function to generate smooth transitions between data points
  const generateSmoothTransitions = (startPoint: DimensionData, endPoint: DimensionData, steps: number): DimensionData[] => {
    const result: DimensionData[] = [];
    
    for (let i = 0; i <= steps; i++) {
      const ratio = i / steps;
      result.push({
        signal: Math.round(startPoint.signal + (endPoint.signal - startPoint.signal) * ratio),
        velocity: Math.round(startPoint.velocity + (endPoint.velocity - startPoint.velocity) * ratio),
        sentimentBalance: Math.round(startPoint.sentimentBalance + (endPoint.sentimentBalance - startPoint.sentimentBalance) * ratio),
        harmDetection: Math.round(startPoint.harmDetection + (endPoint.harmDetection - startPoint.harmDetection) * ratio),
        vulnerability: Math.round(startPoint.vulnerability + (endPoint.vulnerability - startPoint.vulnerability) * ratio),
        rootCauseExpansion: Math.round(startPoint.rootCauseExpansion + (endPoint.rootCauseExpansion - startPoint.rootCauseExpansion) * ratio),
      });
    }
    
    // Remove the last item to avoid duplicating the end point when we append multiple transitions
    return result.slice(0, -1);
  };
  
  // Key data points for each time period
  const keyDataPoints = {
    '7d': [
      { signal: 70, velocity: 85, sentimentBalance: 65, harmDetection: 42, vulnerability: 58, rootCauseExpansion: 74 },
      { signal: 72, velocity: 89, sentimentBalance: 67, harmDetection: 39, vulnerability: 60, rootCauseExpansion: 77 },
      { signal: 74, velocity: 92, sentimentBalance: 68, harmDetection: 38, vulnerability: 61, rootCauseExpansion: 79 },
    ],
    '14d': [
      { signal: 65, velocity: 80, sentimentBalance: 62, harmDetection: 45, vulnerability: 55, rootCauseExpansion: 70 },
      { signal: 68, velocity: 83, sentimentBalance: 64, harmDetection: 43, vulnerability: 57, rootCauseExpansion: 73 },
      { signal: 71, velocity: 87, sentimentBalance: 66, harmDetection: 40, vulnerability: 59, rootCauseExpansion: 75 },
      { signal: 74, velocity: 92, sentimentBalance: 68, harmDetection: 38, vulnerability: 61, rootCauseExpansion: 79 },
    ],
    '30d': [
      { signal: 60, velocity: 75, sentimentBalance: 59, harmDetection: 50, vulnerability: 52, rootCauseExpansion: 65 },
      { signal: 63, velocity: 78, sentimentBalance: 60, harmDetection: 47, vulnerability: 54, rootCauseExpansion: 68 },
      { signal: 66, velocity: 81, sentimentBalance: 63, harmDetection: 44, vulnerability: 56, rootCauseExpansion: 71 },
      { signal: 70, velocity: 85, sentimentBalance: 65, harmDetection: 42, vulnerability: 58, rootCauseExpansion: 74 },
      { signal: 74, velocity: 92, sentimentBalance: 68, harmDetection: 38, vulnerability: 61, rootCauseExpansion: 79 },
    ]
  };
  
  // Generate smooth transitions for each time period
  const generateSmoothData = (keyPoints: DimensionData[]): DimensionData[] => {
    let result: DimensionData[] = [];
    
    for (let i = 0; i < keyPoints.length - 1; i++) {
      const transitionFrames = Math.floor(30 / (keyPoints.length - 1));
      const transitions = generateSmoothTransitions(keyPoints[i], keyPoints[i + 1], transitionFrames);
      result = [...result, ...transitions];
    }
    
    // Add the final keypoint
    result.push(keyPoints[keyPoints.length - 1]);
    
    // Ensure exactly 30 frames
    if (result.length < 30) {
      // Duplicate the last frame if we don't have enough
      const lastFrame = result[result.length - 1];
      while (result.length < 30) {
        result.push({...lastFrame});
      }
    } else if (result.length > 30) {
      // Trim if we have too many
      result = result.slice(0, 30);
    }
    
    return result;
  };
  
  // Mock historical data for animation with smooth transitions (30 frames each)
  const historicalData = useRef<{
    '7d': DimensionData[];
    '14d': DimensionData[];
    '30d': DimensionData[];
  }>({
    '7d': generateSmoothData(keyDataPoints['7d']),
    '14d': generateSmoothData(keyDataPoints['14d']),
    '30d': generateSmoothData(keyDataPoints['30d'])
  });

  // Color mappings for score ranges
  const getScoreColor = (score: number) => {
    if (score >= 80) return '#4c6ef5'; // High - blue
    if (score >= 60) return '#15aabf'; // Medium-high - teal
    if (score >= 40) return '#fab005'; // Medium - yellow
    if (score < 40) return '#fa5252'; // Low - red
  };

  // Load animation data when time range changes or on initial load
  useEffect(() => {
    const data = historicalData.current[timeRange];
    setAnimationData(data);
    setCurrentFrame(0);
    setDimensions(data[0]);
  }, [timeRange]);

  // Animation control functions
  const startAnimation = () => {
    if (isPlaying) return;
    
    stopAnimation(); // Clear any existing interval
    
    setIsPlaying(true);
    timerRef.current = setInterval(() => {
      setCurrentFrame(prev => {
        const nextFrame = (prev + 1) % animationData.length;
        setDimensions(animationData[nextFrame]);
        return nextFrame;
      });
    }, FRAME_DURATION);
  };

  const stopAnimation = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsPlaying(false);
  };

  const resetAnimation = () => {
    stopAnimation();
    setCurrentFrame(0);
    setDimensions(animationData[0]);
  };

  // Handle changing time range
  const handleTimeRangeChange = (range: TimeRange) => {
    stopAnimation();
    setTimeRange(range);
  };
  
  // Clean up animation on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Calculate positioning and sizes for the radar visualization
  const centerX = 250;
  const centerY = 250;
  const maxRadius = 200;
  
  // Calculate points for each dimension on the radar
  const calculatePoint = (value: number, angle: number) => {
    const radius = (value / 100) * maxRadius;
    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);
    return { x, y };
  };

  // Calculate points for all dimensions
  const points = [
    calculatePoint(dimensions.signal, Math.PI / 2 * 3), // Top
    calculatePoint(dimensions.velocity, Math.PI * 5 / 6), // Top-Left 
    calculatePoint(dimensions.vulnerability, Math.PI * 7 / 6), // Bottom-Left
    calculatePoint(dimensions.harmDetection, Math.PI / 2), // Bottom
    calculatePoint(dimensions.rootCauseExpansion, Math.PI / 6), // Bottom-Right
    calculatePoint(dimensions.sentimentBalance, Math.PI * 11 / 6), // Top-Right
  ];

  // Create points string for radar polygon
  const polygonPoints = points.map(p => `${p.x},${p.y}`).join(' ');

  // Descriptions for each dimension
  const dimensionDescriptions = {
    signal: "Strength of pattern signals across all cases",
    velocity: "Rate of change in case volume and pattern emergence",
    sentimentBalance: "Ratio of positive to negative sentiment signals",
    harmDetection: "Detection rate of harmful outcomes in complaints",
    vulnerability: "Prevalence of vulnerability indicators in customer cases",
    rootCauseExpansion: "Growth rate of new root cause patterns"
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-secondary mb-2">Case Radar</h1>
          <p className="text-sm text-secondary/70">Visualizing key dimensions across your case database</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="bg-white border border-gray-200 rounded-lg flex items-center p-1">
            <button 
              className={`px-4 py-1.5 rounded-lg text-sm transition-colors ${timeRange === '7d' ? 'bg-primary text-secondary' : 'text-secondary/70 hover:bg-gray-100'}`}
              onClick={() => handleTimeRangeChange('7d')}
            >
              7 days
            </button>
            <button 
              className={`px-4 py-1.5 rounded-lg text-sm transition-colors ${timeRange === '14d' ? 'bg-primary text-secondary' : 'text-secondary/70 hover:bg-gray-100'}`}
              onClick={() => handleTimeRangeChange('14d')}
            >
              14 days
            </button>
            <button 
              className={`px-4 py-1.5 rounded-lg text-sm transition-colors ${timeRange === '30d' ? 'bg-primary text-secondary' : 'text-secondary/70 hover:bg-gray-100'}`}
              onClick={() => handleTimeRangeChange('30d')}
            >
              30 days
            </button>
          </div>
          <div className="flex space-x-2">
            {isPlaying ? (
              <button 
                className="p-2 bg-rose-100 text-rose-600 rounded-full hover:bg-rose-200 transition-colors"
                onClick={stopAnimation}
                title="Pause animation"
              >
                <IconWrapper icon={FiPause} size={20} />
              </button>
            ) : (
              <button 
                className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                onClick={startAnimation}
                title="Play animation"
              >
                <IconWrapper icon={FiPlay} size={20} />
              </button>
            )}
            <button 
              className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors"
              onClick={resetAnimation}
              title="Reset animation"
            >
              <IconWrapper icon={FiRefreshCw} size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Radar Visualization */}
        <div className="lg:col-span-2 bg-[#151c38] rounded-xl p-6 h-[550px] flex flex-col items-center justify-center relative">
          <div className="absolute top-4 left-4 text-white text-sm">
            <span className="font-semibold">{timeRange === '7d' ? 'Past 7 Days' : timeRange === '14d' ? 'Past 14 Days' : 'Past 30 Days'}</span>
            <div className="text-white/70">Frame {currentFrame + 1} of 30</div>
          </div>
          <svg width="500" height="500" viewBox="0 0 500 500">
            {/* Background hexagon and gridlines */}
            <defs>
              <radialGradient id="radarGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                <stop offset="0%" stopColor="#ff6b9c" stopOpacity="0.8" />
                <stop offset="50%" stopColor="#6978ff" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#4237bf" stopOpacity="0.4" />
              </radialGradient>
              <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="10" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>
            
            {/* Outer hexagon */}
            <polygon 
              points="250,50 400,125 400,375 250,450 100,375 100,125" 
              fill="none" 
              stroke="#ffffff" 
              strokeOpacity="0.2" 
              strokeWidth="1"
            />
            
            {/* Middle hexagon */}
            <polygon 
              points="250,100 350,150 350,350 250,400 150,350 150,150" 
              fill="none" 
              stroke="#ffffff" 
              strokeOpacity="0.15" 
              strokeWidth="1"
            />
            
            {/* Inner hexagon */}
            <polygon 
              points="250,150 325,187.5 325,312.5 250,350 175,312.5 175,187.5" 
              fill="none" 
              stroke="#ffffff" 
              strokeOpacity="0.1" 
              strokeWidth="1"
            />
            
            {/* Center hexagon */}
            <polygon 
              points="250,200 300,225 300,275 250,300 200,275 200,225" 
              fill="none" 
              stroke="#ffffff" 
              strokeOpacity="0.05" 
              strokeWidth="1"
            />
            
            {/* Axes */}
            <line x1="250" y1="50" x2="250" y2="450" stroke="#ffffff" strokeOpacity="0.2" strokeWidth="1" />
            <line x1="100" y1="125" x2="400" y2="375" stroke="#ffffff" strokeOpacity="0.2" strokeWidth="1" />
            <line x1="100" y1="375" x2="400" y2="125" stroke="#ffffff" strokeOpacity="0.2" strokeWidth="1" />
            
            {/* Data polygon */}
            <polygon 
              points={polygonPoints}
              fill="url(#radarGradient)"
              stroke="#ffffff"
              strokeWidth="2"
              strokeOpacity="0.8"
              filter="url(#glow)"
            />
            
            {/* Points on vertices */}
            {points.map((point, i) => (
              <circle 
                key={i} 
                cx={point.x} 
                cy={point.y} 
                r="6" 
                fill="#ffffff" 
              />
            ))}
            
            {/* Dimension labels with values */}
            <text x="250" y="30" textAnchor="middle" fill="#ffffff" fontSize="14" fontWeight="bold">Signal ({dimensions.signal})</text>
            <text x="450" y="125" textAnchor="start" fill="#ffffff" fontSize="14" fontWeight="bold">Sentiment Balance ({dimensions.sentimentBalance})</text>
            <text x="450" y="375" textAnchor="start" fill="#ffffff" fontSize="14" fontWeight="bold">Root Cause Expansion ({dimensions.rootCauseExpansion})</text>
            <text x="250" y="470" textAnchor="middle" fill="#ffffff" fontSize="14" fontWeight="bold">Harm Detection ({dimensions.harmDetection})</text>
            <text x="50" y="375" textAnchor="end" fill="#ffffff" fontSize="14" fontWeight="bold">Vulnerability ({dimensions.vulnerability})</text>
            <text x="50" y="125" textAnchor="end" fill="#ffffff" fontSize="14" fontWeight="bold">Velocity ({dimensions.velocity})</text>
            
            {/* Data points */}
            <circle cx="250" cy="50" r="16" fill="#4056f4" stroke="#ffffff" strokeWidth="2">
              <title>Signal: {dimensions.signal}</title>
            </circle>
            <text x="250" y="55" textAnchor="middle" fill="#ffffff" fontSize="12" fontWeight="bold">{dimensions.signal}</text>
            
            <circle cx="100" cy="125" r="16" fill="#4056f4" stroke="#ffffff" strokeWidth="2">
              <title>Velocity: {dimensions.velocity}</title>
            </circle>
            <text x="100" y="130" textAnchor="middle" fill="#ffffff" fontSize="12" fontWeight="bold">{dimensions.velocity}</text>
            
            <circle cx="100" cy="375" r="16" fill="#4056f4" stroke="#ffffff" strokeWidth="2">
              <title>Vulnerability: {dimensions.vulnerability}</title>
            </circle>
            <text x="100" y="380" textAnchor="middle" fill="#ffffff" fontSize="12" fontWeight="bold">{dimensions.vulnerability}</text>
            
            <circle cx="250" cy="450" r="16" fill="#4056f4" stroke="#ffffff" strokeWidth="2">
              <title>Harm Detection: {dimensions.harmDetection}</title>
            </circle>
            <text x="250" y="455" textAnchor="middle" fill="#ffffff" fontSize="12" fontWeight="bold">{dimensions.harmDetection}</text>
            
            <circle cx="400" cy="375" r="16" fill="#4056f4" stroke="#ffffff" strokeWidth="2">
              <title>Root Cause Expansion: {dimensions.rootCauseExpansion}</title>
            </circle>
            <text x="400" y="380" textAnchor="middle" fill="#ffffff" fontSize="12" fontWeight="bold">{dimensions.rootCauseExpansion}</text>
            
            <circle cx="400" cy="125" r="16" fill="#4056f4" stroke="#ffffff" strokeWidth="2">
              <title>Sentiment Balance: {dimensions.sentimentBalance}</title>
            </circle>
            <text x="400" y="130" textAnchor="middle" fill="#ffffff" fontSize="12" fontWeight="bold">{dimensions.sentimentBalance}</text>
          </svg>
        </div>
        
        {/* Metrics cards and explanation */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-start mb-4">
              <div className="rounded-lg p-2 bg-blue-100 text-blue-600 mr-3">
                <IconWrapper icon={FiInfo} size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-secondary mb-1">Understanding Case Radar</h3>
                <p className="text-sm text-secondary/70">
                  The Case Radar visualizes six critical dimensions of your complaint database. Higher scores indicate 
                  stronger signals or higher prevalence of each dimension across your cases.
                </p>
              </div>
            </div>
          </div>
          
          {/* Dimension cards */}
          {Object.entries(dimensions).map(([key, value]) => {
            const dimensionKey = key as keyof typeof dimensionDescriptions;
            return (
              <div key={key} className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex justify-between items-center mb-1">
                  <h3 className="font-medium text-secondary capitalize">{key.replace(/([A-Z])/g, ' $1')}</h3>
                  <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    value >= 80 ? 'bg-blue-100 text-blue-600' :
                    value >= 60 ? 'bg-teal-100 text-teal-600' :
                    value >= 40 ? 'bg-amber-100 text-amber-600' :
                    'bg-red-100 text-red-600'
                  }`}>
                    {value}
                  </div>
                </div>
                <p className="text-xs text-secondary/70">{dimensionDescriptions[dimensionKey]}</p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className="h-2 rounded-full" 
                    style={{ 
                      width: `${value}%`, 
                      backgroundColor: getScoreColor(value)
                    }}
                  ></div>
                </div>
              </div>
            );
          })}
          
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <h3 className="font-medium text-secondary mb-2">Actions</h3>
            <div className="grid grid-cols-2 gap-2">
              <button className="px-3 py-2 text-sm bg-primary text-secondary rounded-lg hover:bg-primary/90 transition-colors">
                Export Report
              </button>
              <button className="px-3 py-2 text-sm bg-white border border-gray-200 text-secondary rounded-lg hover:bg-gray-50 transition-colors">
                Set Alerts
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Overall Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 md:col-span-2">
          <h2 className="text-lg font-semibold text-secondary mb-3">Radar Insights</h2>
          <div className="space-y-3">
            <p className="text-secondary">
              Your case database currently shows <span className="font-semibold text-blue-600">high velocity</span> of new complaints
              with moderate sentiment balance. The harm detection score is relatively low, indicating fewer harmful outcomes
              detected in recent complaints.
            </p>
            <p className="text-secondary">
              The <span className="font-semibold text-amber-600">root cause expansion</span> and <span className="font-semibold text-blue-600">signal strength</span> metrics 
              suggest emerging patterns that may require proactive monitoring. Consider investigating the high velocity
              dimension to identify potential process improvements.
            </p>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-secondary mb-3">Dimension Trends</h2>
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-secondary/70">Signal</span>
                <span className="text-secondary font-medium">+3%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-1.5 w-full bg-blue-500 rounded-full"></div>
                <div className="h-1.5 w-3 bg-blue-700 rounded-full"></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-secondary/70">Velocity</span>
                <span className="text-secondary font-medium">+12%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-1.5 w-full bg-blue-500 rounded-full"></div>
                <div className="h-1.5 w-12 bg-blue-700 rounded-full"></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-secondary/70">Harm Detection</span>
                <span className="text-secondary font-medium">-5%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-1.5 w-full bg-red-300 rounded-full"></div>
                <div className="h-1.5 w-5 bg-red-500 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RadarView;